<script setup lang="ts">

import { T } from '@tolgee/vue'
import { useMysteryBoxes, useOpenMysteryBox } from '@/composables/game/mystery-boxes'
import MysteryBoxCard from '@/components/MysteryBoxCard.vue'
import { computed, ref } from 'vue'
import {
  ECOMMERCE_ITEM_PRICE_TYPE,
  type EcommerceItem,
  MYSTERY_BOX_TYPE,
  type MysteryBoxReward,
} from '@/types/shared/player-config'
import type { MysteryBoxItem, OpenedMysteryBoxReward } from '@/types/mystery-box'
import MysteryBoxRewardsModal from '@/components/modals/MysteryBoxRewardsModal.vue'
import MysteryBoxPurchaseModal from '@/components/modals/MysteryBoxPurchaseModal.vue'
import { syncPlayer } from '@/composables/game/player'
import { useConfetti } from '@/composables/game/confetti'
import MysteryBoxPurchaseSuccessfulModal from '@/components/modals/MysteryBoxPurchaseSuccessfulModal.vue'
import MysteryBoxOpeningOverlay from '@/components/overlays/MysteryBoxOpeningOverlay.vue'
import { useWatchOverlays } from '@/composables/helpers/scoll'
import MysteryBoxResultModal from '@/components/modals/MysteryBoxResultModal.vue'
import { useEcommerce } from '@/composables/game/ecommerce'
import { useToaster } from '@/composables/game/toasters'
import DefaultDiscountTag from '@/components/periodic/default-discount/DefaultDiscountTag.vue'
import { useShop } from '@/composables/game/shop'
import MysteryBoxImage from '@/assets/images/gifs/mystery-box.gif'
import IWDMysteryBoxImage from '@/assets/images/periodic/IWD/IWD-mystery-box.gif'
import STPMysteryBoxImage from '@/assets/images/periodic/STP/STP-mystery-box.gif'
import IWDBackground from '@/assets/images/periodic/IWD/IWD-background.png'
import STPBackground from '@/assets/images/periodic/STP/STP-background.png'
import DefaultDiscountBanner from '@/components/periodic/default-discount/DefaultDiscountBanner.vue'
import MysteryBoxBackground from '@/assets/images/mystery-bg.png'
import IWDBanner from '@/components/periodic/IWD/IWDBanner.vue'
import IWDTag from '@/components/periodic/IWD/IWDTag.vue'
import DefaultBOGOTag from '@/components/periodic/default-BOGO/DefaultBOGOTag.vue'
import DefaultBOGOBanner from '@/components/periodic/default-BOGO/DefaultBOGOBanner.vue'
import DefaultBOGOBackground from '@/assets/images/periodic/default-BOGO/default-BOGO-background.png'
import DefaultBOGXBackground from '@/assets/images/periodic/default-BOGX/default-BOGX-background.png'
import STPBanner from '@/components/periodic/STP/STPBanner.vue'
import STPTag from '@/components/periodic/STP/STPTag.vue'
import DefaultBOGXBanner from '@/components/periodic/default-BOGX/DefaultBOGXBanner.vue'
import DefaultBOGXTag from '@/components/periodic/default-BOGX/DefaultBOGXTag.vue'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import ElementMultiplierRevealedOverlay from '@/components/overlays/ElementMultiplierRevealedOverlay.vue'
import router from '@/router'
import { RouteName } from '@/routes'

const {
  _mysteryBoxes,
  canHaveSale,
  unopenedMysteryBoxes,
  sortMysteryBoxRewards,
} = useMysteryBoxes()
const { showConfetti } = useConfetti()
const { openBox, openingBoxType, showElementMultiplierRevealedOverlay } = useOpenMysteryBox()
const { purchase } = useEcommerce()
const { notify } = useToaster()
const {
  saleName,
  countdownForSaleEnds,
  isIWD,
  isDefaultDiscount,
  isDefaultBOGO,
  isSTP,
  isDefaultBOGX,
  saleValue,
} = useShop()
const { topNumber } = useFullScreenDimensions()

const mysteryBoxForRewardsModal = ref<MysteryBoxItem | null>(null)
const mysteryBoxForPurchaseModal = ref<MysteryBoxItem | null>(null)
const mysteryBoxForPurchaseSuccessModal = ref<null | { box: MysteryBoxItem, ecommerceItem: EcommerceItem }>(null)
const openedReward = ref<{ rewards: MysteryBoxReward[] | null, boxType: MYSTERY_BOX_TYPE | null }>({
  rewards: null,
  boxType: null,
})
const openedRewardItems = computed((): MysteryBoxReward[] | null => openedReward.value.rewards)
const showMysteryBoxRewardResultModal = ref(false)
const showOpeningAnimation = ref(false)
const loadingEcommerceItemId = ref<string | null>(null)

const topStyles = computed(() => {
  return { top: `-${topNumber.value + 24}px` }
})

async function purchaseItem(box: MysteryBoxItem, item: EcommerceItem) {
  loadingEcommerceItemId.value = item.id
  try {
    const { paid } = await purchase({
      item: item,
      priceType: ECOMMERCE_ITEM_PRICE_TYPE.STAR,
      type: 'item',
    })
    if (paid) {
      await syncPlayer()
      await openMysteryBox(box.boxType)
      loadingEcommerceItemId.value = null
    } else {
      loadingEcommerceItemId.value = null
    }
  } catch (e) {
    loadingEcommerceItemId.value = null
  }
}

async function onPurchase(box: MysteryBoxItem, ecommerceItem: EcommerceItem) {
  if (!box || !ecommerceItem) return
  mysteryBoxForPurchaseModal.value = null
  mysteryBoxForPurchaseSuccessModal.value = { box, ecommerceItem }
  setTimeout(() => showConfetti({ spread: 220, count: 90 }), 100)
  await syncPlayer()
}

async function openMysteryBox(boxType: MYSTERY_BOX_TYPE) {
  resetOpenedReward()
  mysteryBoxForPurchaseSuccessModal.value = null
  showOpeningAnimation.value = true
  openedReward.value = { rewards: await openBox(boxType), boxType: boxType }
  if (!openedReward.value) {
    showOpeningAnimation.value = false
  }
}

async function finishBoxOpen() {
  showOpeningAnimation.value = false
  showMysteryBoxRewardResultModal.value = true
  notify({ title: 'Item added!', type: 'success' })
}

function resetOpenedReward() {
  openedReward.value = { rewards: null, boxType: null }
}

function onResultModalHide() {
  resetOpenedReward()
}

function hideElementMultiplierOverlay() {
  showElementMultiplierRevealedOverlay.value = false
  router.push({ name: RouteName.PROFILE })
}

useWatchOverlays(computed(() => showOpeningAnimation.value))

</script>

<template>
  <div class="flex-1 flex flex-col w-full items-center px-4 pb-[100px]">
    <img
      v-if="isIWD && canHaveSale"
      :src="IWDBackground"
      class="absolute w-full top-0 left-0 right-0 h-[400px] object-fill z-[1]"
    >
    <img
      v-else-if="isSTP && canHaveSale"
      :src="STPBackground"
      class="absolute w-full top-0 left-0 right-0 h-[400px] object-fill z-[1]"
    >
    <img
      v-else-if="isDefaultBOGO && canHaveSale"
      :src="DefaultBOGOBackground"
      class="absolute w-full top-0 left-0 right-0 h-[400px] object-fill z-[1]"
    >
    <img
      v-else-if="isDefaultBOGX && canHaveSale"
      :src="DefaultBOGXBackground"
      class="absolute w-full top-0 left-0 right-0 h-[400px] object-fill z-[1]"
    >
    <img
      v-else
      :src="MysteryBoxBackground"
      class="absolute w-full top-0 left-0 right-0 h-[400px] object-fill z-[1]"
    >
    <div class="w-full relative z-[2] mt-6 flex flex-col items-center">
      <div
        v-if="canHaveSale"
        class="absolute w-screen overflow-hidden h-[300px] -left-4 pointer-events-none flex justify-center"
        :style="topStyles"
      >
        <DefaultBOGOBanner
          v-if="isDefaultBOGO"
          :name="saleName"
          :countdown="countdownForSaleEnds"
        />
        <DefaultBOGXBanner
          v-if="isDefaultBOGX"
          :name="saleName"
          :countdown="countdownForSaleEnds"
        />
        <DefaultDiscountBanner
          v-if="isDefaultDiscount"
          :name="saleName"
          :countdown="countdownForSaleEnds"
        />
        <IWDBanner
          v-else-if="isIWD"
          :name="saleName"
          :countdown="countdownForSaleEnds"
        />
        <STPBanner
          v-else-if="isSTP"
          :name="saleName"
          :countdown="countdownForSaleEnds"
        />
      </div>
      <template
        v-if="canHaveSale"
      >
        <DefaultBOGOTag
          v-if="isDefaultBOGO"
          class="absolute left-[calc(50%+70px)] top-[60px] z-[10]"
        />
        <DefaultBOGXTag
          v-if="isDefaultBOGX"
          class="absolute left-[calc(50%+70px)] top-[60px] z-[10]"
          :amount="saleValue"
        />
        <DefaultDiscountTag
          v-if="isDefaultDiscount"
          class="absolute left-[calc(50%+70px)] top-[80px] z-[10]"
        />
        <IWDTag
          v-if="isIWD"
          class="absolute left-[calc(50%+70px)] top-[80px] z-[10]"
        />
        <STPTag
          v-else-if="isSTP"
          class="absolute left-[calc(50%+70px)] top-[80px] z-[10]"
        />
      </template>

      <div class="relative w-[174px] h-[120px] flex items-center justify-center">
        <img
          v-if="isIWD && canHaveSale"
          :src="IWDMysteryBoxImage"
          class="w-full h-auto object-cover"
        >
        <img
          v-else-if="isSTP && canHaveSale"
          :src="STPMysteryBoxImage"
          class="w-full h-auto object-cover"
        >
        <img
          v-else
          :src="MysteryBoxImage"
          class="w-full h-auto object-cover"
        >
        <img
          class="absolute left-1/2 -translate-x-1/2 shadow-question-mark top-[70px] w-[34px] h-auto "
          src="@/assets/images/question-mark2.png"
        >
      </div>
      <div class="mt-6 text-light-white text-[32px] leading-[32px] font-bold uppercase">
        <T
          key-name="mystery_boxes.title"
          default-value="Mystery Boxes"
        />
      </div>
      <div class="text-center text-light-white/70 text-base font-medium -tracking-[0.32px]">
        <T
          key-name="mystery_boxes.description"
          default-value="Unlock surprises that boost your journey"
        />
      </div>
      <div class="mt-6 w-full flex flex-col gap-y-4">
        <MysteryBoxCard
          v-for="mysteryBox in _mysteryBoxes"
          :key="mysteryBox.boxType"
          class="scale-in"
          :class="{'pointer-events-none': openingBoxType === mysteryBox.boxType}"
          :name="mysteryBox.name"
          :image="mysteryBox.image"
          :description="mysteryBox.description"
          :ecommerce-items="mysteryBox.ecommerceItems"
          :unopened-count="unopenedMysteryBoxes[mysteryBox.boxType]"
          :is-loading="loadingEcommerceItemId !== null && mysteryBox.ecommerceItems.map(i => i.id).includes(loadingEcommerceItemId)"
          @click="mysteryBoxForRewardsModal = sortMysteryBoxRewards(mysteryBox)"
          @buy="mysteryBoxForPurchaseModal = mysteryBox"
          @buy-free="purchaseItem(mysteryBox, $event)"
          @open="openMysteryBox(mysteryBox.boxType)"
        />
      </div>
    </div>

    <MysteryBoxRewardsModal
      v-if="Boolean(mysteryBoxForRewardsModal)"
      :show="Boolean(mysteryBoxForRewardsModal)"
      :item="mysteryBoxForRewardsModal"
      @hide="mysteryBoxForRewardsModal = null"
    />

    <MysteryBoxPurchaseModal
      v-if="Boolean(mysteryBoxForPurchaseModal)"
      :show="Boolean(mysteryBoxForPurchaseModal)"
      :item="mysteryBoxForPurchaseModal"
      @hide="mysteryBoxForPurchaseModal = null"
      @on-purchase="onPurchase(mysteryBoxForPurchaseModal as MysteryBoxItem, $event)"
    />

    <MysteryBoxPurchaseSuccessfulModal
      v-if="Boolean(mysteryBoxForPurchaseSuccessModal)"
      :item="mysteryBoxForPurchaseSuccessModal"
      :show="Boolean(mysteryBoxForPurchaseSuccessModal)"
      @hide="mysteryBoxForPurchaseSuccessModal = null"
      @open="openMysteryBox"
    />

    <MysteryBoxOpeningOverlay
      v-if="showOpeningAnimation"
      :reward="openedRewardItems"
      @finish="finishBoxOpen"
    />

    <MysteryBoxResultModal
      v-if="showMysteryBoxRewardResultModal && openedReward.rewards && openedReward.boxType"
      :show="showMysteryBoxRewardResultModal"
      :item="openedReward as OpenedMysteryBoxReward"
      :unopened-boxes="unopenedMysteryBoxes"
      @open="showMysteryBoxRewardResultModal = false; openMysteryBox($event)"
      @hide="showMysteryBoxRewardResultModal = false; onResultModalHide();"
    />

    <ElementMultiplierRevealedOverlay
      v-if="showElementMultiplierRevealedOverlay"
      :show="showElementMultiplierRevealedOverlay"
      @click="hideElementMultiplierOverlay"
    />
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/styles/sales.scss';

.shadow-question-mark {
	filter: drop-shadow(0px 0px 22.857px #FFFFFF);
}
</style>
